import * as React from "react";
import { useEffect, useRef, useState } from "react";
import {
  Linking,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  Text,
  View,
  type TextStyle,
  type ViewStyle,
} from "react-native";
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";
import Svg, { Circle, Path, Rect } from "react-native-svg";

import { COLORS } from "../../lib/brand";
import { PANE_LABELS, ROUTE_MAP, type PaneKey } from "../../lib/brand-routes";

/* ─────────────────────── Palette ─────────────────────── */

const PAGE_BG = "#141414";
const HEADER_BG = "#1A1A1A";
const HEADER_BORDER = "rgba(181,135,58,0.55)";
const BUTTON_BORDER = "rgba(247,244,238,0.14)";
const BUTTON_HOVER_BG = "rgba(247,244,238,0.06)";
const TEXT = COLORS.LINEN.hex;
const TEXT_DIM = "rgba(247,244,238,0.66)";
const TEXT_FAINT = "rgba(247,244,238,0.36)";
const CONNECTOR = "rgba(247,244,238,0.45)";
const AMBER = COLORS.AMBER.hex;
const AMBER_BG_ACTIVE = "rgba(181,135,58,0.18)";
const AMBER_BORDER_ACTIVE = "rgba(181,135,58,0.55)";
const ADMIN_RED = "#B5453A";

const MONO_FONT: TextStyle["fontFamily"] = Platform.select({
  web: 'Menlo, Consolas, "Courier New", monospace',
  ios: "Menlo",
  android: "monospace",
  default: "monospace",
});

type PaneOrAux = PaneKey | "Aux";

const PANE_ACCENT: Record<PaneOrAux, string> = {
  General: COLORS.AMBER.hex,
  Student: COLORS.SAGE.hex,
  Client: COLORS.FOREST.hex,
  Admin: ADMIN_RED,
  Judge: COLORS.SAGE_DEEP.hex,
  Aux: "rgba(247,244,238,0.40)",
};

/* ─────────────────────── Modes ─────────────────────── */

type FlowMode = "all" | "student" | "managed" | "licensed" | "admin" | "judge";

const MODES: Array<{ id: FlowMode; label: string }> = [
  { id: "all", label: "All" },
  { id: "student", label: "Student" },
  { id: "managed", label: "Managed Client" },
  { id: "licensed", label: "Licensed Client" },
  { id: "admin", label: "Admin" },
  { id: "judge", label: "Judge" },
];

/* ─────────────────────── Tree data ─────────────────────── */

type SubItem = { label: string; endpoint?: string };

type TreeNode = {
  id: string;
  title: string;
  pane: PaneOrAux;
  path?: string;
  external?: boolean;
  externalUrl?: string;
  overlays?: SubItem[];
  children?: TreeNode[];
};

/**
 * ALL — full cross-persona flow. Landing branches to Public Pages, Sign Up,
 * and Login + MFA; Login fans out to every portal, each of which drills into
 * its own multi-step workspace. Mirrors the depth pattern used by the
 * per-persona trees so nothing is hidden behind a single chevron.
 */
const ALL_TREE: TreeNode = {
  id: "all-landing",
  title: "Landing",
  pane: "General",
  path: "/",
  overlays: [
    { label: "Hero + product story", endpoint: "/" },
    { label: "Brand reference (this page)", endpoint: "/brand" },
  ],
  children: [
    {
      id: "all-public",
      title: "Public Pages",
      pane: "Aux",
      overlays: [
        { label: "Pricing", endpoint: "/pricing" },
        { label: "FAQ", endpoint: "/faq" },
        { label: "Contact / Book intake", endpoint: "/contact" },
        { label: "Privacy", endpoint: "/privacy" },
        { label: "Terms", endpoint: "/terms" },
      ],
    },
    {
      id: "all-signup",
      title: "Sign Up",
      pane: "General",
      path: "/signup",
      overlays: [
        { label: "Student signup", endpoint: "/signup/student" },
        { label: "Client signup", endpoint: "/signup/client" },
        { label: "Judge signup", endpoint: "/signup/judge" },
        { label: "Employee signup (invite-only)", endpoint: "/signup/employee" },
        { label: "Accept invite landing", endpoint: "/accept-invite" },
      ],
    },
    {
      id: "all-login",
      title: "Login + MFA",
      pane: "General",
      path: "/login",
      overlays: [
        { label: "MFA challenge", endpoint: "/login/mfa-challenge" },
        { label: "Forgot password", endpoint: "/forgot-password" },
        { label: "Reset password", endpoint: "/reset-password" },
        { label: "Confirm email", endpoint: "/confirm" },
      ],
      children: [
        {
          id: "all-student",
          title: "Student Portal",
          pane: "Student",
          path: "/student",
          overlays: [
            { label: "Activity overlay" },
            { label: "Settings overlay" },
            { label: "Profile overlay" },
            { label: "Search overlay" },
          ],
          children: [
            {
              id: "all-s-find",
              title: "Find Competition",
              pane: "Student",
              path: "/student/find",
              children: [
                {
                  id: "all-s-ws",
                  title: "Competition Workspace",
                  pane: "Student",
                  path: "/student/competition/[id]",
                  overlays: [
                    { label: "Team tab", endpoint: "/student/competition/[id]/team" },
                    { label: "Info tab", endpoint: "/student/competition/[id]/info" },
                    { label: "Announcements" },
                  ],
                  children: [
                    {
                      id: "all-s-submit",
                      title: "Submit Work",
                      pane: "Student",
                      path: "/student/competition/[id]/deliverables",
                    },
                  ],
                },
              ],
            },
            {
              id: "all-s-teams",
              title: "Teams",
              pane: "Student",
              path: "/student/teams",
              overlays: [
                { label: "Team chat", endpoint: "/student/competition/[id]/team" },
                { label: "Manage roster" },
                { label: "Invite link" },
              ],
            },
            {
              id: "all-s-subs",
              title: "Submissions",
              pane: "Student",
              path: "/student/submissions",
            },
            {
              id: "all-s-career",
              title: "Career Interests",
              pane: "Student",
              path: "/student/career-interests",
            },
            {
              id: "all-s-offers",
              title: "Interview Offers",
              pane: "Student",
              path: "/student/offers",
            },
            {
              id: "all-s-winnings",
              title: "Winnings",
              pane: "Student",
              path: "/student/winnings",
              overlays: [
                { label: "Tax info (W-9)", endpoint: "/student/tax-info" },
              ],
            },
          ],
        },
        {
          id: "all-client",
          title: "Client Portal",
          pane: "Client",
          path: "/client",
          overlays: [
            { label: "Welcome (first-time)", endpoint: "/client/welcome" },
            { label: "All candidates", endpoint: "/client/candidates" },
            { label: "Your company", endpoint: "/client/company" },
            { label: "Team", endpoint: "/client/team" },
            { label: "Billing", endpoint: "/client/billing" },
            { label: "Settings", endpoint: "/client/settings" },
            { label: "Notifications", endpoint: "/client/notifications" },
          ],
          children: [
            {
              id: "all-c-programs",
              title: "Programs Grid",
              pane: "Client",
              path: "/client",
              children: [
                {
                  id: "all-c-intake",
                  title: "Book Intake (RM)",
                  pane: "Client",
                  external: true,
                  externalUrl: "https://outlook.office.com/book/CoravuIntake@Coravu.com/",
                },
                {
                  id: "all-c-build",
                  title: "Build Brief",
                  pane: "Client",
                  path: "/client/competition/new",
                  children: [
                    {
                      id: "all-c-edit",
                      title: "Edit Case",
                      pane: "Client",
                      path: "/client/competition/[id]/edit",
                      overlays: [
                        { label: "Deliverables list", endpoint: "/client/competition/[id]/deliverables" },
                        { label: "New deliverable", endpoint: "/client/competition/[id]/deliverable/new" },
                        { label: "Rubric weights editor" },
                        { label: "Positions", endpoint: "/client/competition/[id]/positions" },
                      ],
                    },
                  ],
                },
                {
                  id: "all-c-dash",
                  title: "Live Dashboard",
                  pane: "Client",
                  path: "/client/competition/[id]/dashboard",
                  children: [
                    {
                      id: "all-c-cand",
                      title: "Candidates",
                      pane: "Client",
                      path: "/client/competition/[id]/candidates",
                    },
                    {
                      id: "all-c-pipe",
                      title: "Pipeline",
                      pane: "Client",
                      path: "/client/competition/[id]/pipeline",
                    },
                    {
                      id: "all-c-short",
                      title: "Shortlist",
                      pane: "Client",
                      path: "/client/competition/[id]/shortlist",
                    },
                  ],
                },
              ],
            },
          ],
        },
        {
          id: "all-admin",
          title: "Admin Portal",
          pane: "Admin",
          path: "/admin",
          overlays: [
            { label: "Global search" },
            { label: "Notifications" },
            { label: "Account & security", endpoint: "/admin/account" },
          ],
          children: [
            {
              id: "all-a-inbox",
              title: "Inbox",
              pane: "Admin",
              path: "/admin/inquiries",
              overlays: [
                { label: "Case approvals", endpoint: "/admin/case-approvals" },
                { label: "Invites", endpoint: "/admin/invites" },
                { label: "Pending", endpoint: "/admin/pending" },
                { label: "Rejected", endpoint: "/admin/rejected" },
                { label: "Student review", endpoint: "/admin/students/review" },
              ],
            },
            {
              id: "all-a-crm",
              title: "CRM",
              pane: "Admin",
              path: "/admin/students",
              overlays: [
                { label: "Companies", endpoint: "/admin/clients" },
                { label: "Judges", endpoint: "/admin/judges" },
                { label: "Calendar", endpoint: "/admin/calendar" },
                { label: "Campus admins", endpoint: "/admin/campus-admins" },
              ],
            },
            {
              id: "all-a-programs",
              title: "Programs",
              pane: "Admin",
              path: "/admin/competitions",
              overlays: [
                { label: "Winners", endpoint: "/admin/competitions/[id]/winners" },
                { label: "Announcements", endpoint: "/admin/announcements" },
                { label: "Disbursements", endpoint: "/admin/disbursements" },
              ],
              children: [
                {
                  id: "all-a-rm",
                  title: "Build RM Program",
                  pane: "Admin",
                  path: "/admin/programs/new-rm",
                },
              ],
            },
            {
              id: "all-a-platform",
              title: "Platform",
              pane: "Admin",
              path: "/admin/audit-log",
              overlays: [
                { label: "Users + roles", endpoint: "/admin/users" },
                { label: "Internal team", endpoint: "/admin/team" },
                { label: "Security", endpoint: "/admin/security" },
                { label: "MFA enroll", endpoint: "/admin/mfa/enroll" },
                { label: "Email domains", endpoint: "/admin/domains" },
                { label: "Promo codes", endpoint: "/admin/promos" },
              ],
            },
          ],
        },
        {
          id: "all-judge",
          title: "Judge Portal",
          pane: "Judge",
          path: "/judge",
          overlays: [
            { label: "Assignments queue", endpoint: "/judge" },
            { label: "Profile", endpoint: "/judge/profile" },
            { label: "Settings & notification prefs", endpoint: "/judge/settings" },
          ],
          children: [
            {
              id: "all-judge-comp",
              title: "Competition Overview",
              pane: "Judge",
              path: "/judge/competition/[id]",
              overlays: [
                { label: "Student profile (read-only)", endpoint: "/judge/student/[id]" },
              ],
              children: [
                {
                  id: "all-judge-score",
                  title: "Score Submission",
                  pane: "Judge",
                  path: "/judge/competition/[id]/score/[submissionId]",
                },
              ],
            },
          ],
        },
      ],
    },
  ],
};

/**
 * STUDENT — Sign Up → Login → Home → opportunities → Specific Competition Workspace → Submit.
 */
const STUDENT_TREE: TreeNode = {
  id: "s-signup",
  title: "Sign Up",
  pane: "General",
  path: "/signup/student",
  children: [
    {
      id: "s-login",
      title: "Login + MFA",
      pane: "General",
      path: "/login",
      overlays: [
        { label: "MFA challenge", endpoint: "/login/mfa-challenge" },
        { label: "Forgot password", endpoint: "/forgot-password" },
        { label: "Confirm email", endpoint: "/confirm" },
      ],
      children: [
        {
          id: "s-home",
          title: "Student Home",
          pane: "Student",
          path: "/student",
          overlays: [
            { label: "Activity overlay" },
            { label: "Settings overlay" },
            { label: "Profile overlay" },
            { label: "Search overlay" },
          ],
          children: [
            {
              id: "s-find",
              title: "Find Competition",
              pane: "Student",
              path: "/student/find",
              children: [
                {
                  id: "s-workspace",
                  title: "Competition Workspace",
                  pane: "Student",
                  path: "/student/competition/[id]",
                  overlays: [
                    { label: "Team tab", endpoint: "/student/competition/[id]/team" },
                    { label: "Info tab", endpoint: "/student/competition/[id]/info" },
                    { label: "Announcements" },
                  ],
                  children: [
                    {
                      id: "s-submit",
                      title: "Submit Work",
                      pane: "Student",
                      path: "/student/competition/[id]/deliverables",
                    },
                  ],
                },
              ],
            },
            {
              id: "s-teams",
              title: "Teams",
              pane: "Student",
              path: "/student/teams",
              overlays: [
                { label: "Team chat", endpoint: "/student/competition/[id]/team" },
                { label: "Manage roster" },
                { label: "Invite link" },
              ],
            },
            {
              id: "s-subs",
              title: "Submissions",
              pane: "Student",
              path: "/student/submissions",
            },
            {
              id: "s-career",
              title: "Career Interests",
              pane: "Student",
              path: "/student/career-interests",
            },
          ],
        },
      ],
    },
  ],
};

/**
 * LICENSED CLIENT — Sign Up → Login → Welcome → Programs Grid → Build Brief → Edit Case
 *                                                              → Live Dashboard → [Candidates, Pipeline, Shortlist]
 */
const LICENSED_TREE: TreeNode = {
  id: "l-signup",
  title: "Sign Up",
  pane: "General",
  path: "/signup/client",
  children: [
    {
      id: "l-login",
      title: "Login + MFA",
      pane: "General",
      path: "/login",
      overlays: [
        { label: "MFA challenge", endpoint: "/login/mfa-challenge" },
        { label: "Forgot password", endpoint: "/forgot-password" },
      ],
      children: [
        {
          id: "l-welcome",
          title: "Welcome",
          pane: "Client",
          path: "/client/welcome",
          children: [
            {
              id: "l-programs",
              title: "Programs Grid",
              pane: "Client",
              path: "/client",
              overlays: [
                { label: "Candidates", endpoint: "/client/candidates" },
                { label: "Your company", endpoint: "/client/company" },
                { label: "Team", endpoint: "/client/team" },
                { label: "Billing", endpoint: "/client/billing" },
                { label: "Settings", endpoint: "/client/settings" },
                { label: "Notifications", endpoint: "/client/notifications" },
              ],
              children: [
                {
                  id: "l-new",
                  title: "Build Brief",
                  pane: "Client",
                  path: "/client/competition/new",
                  children: [
                    {
                      id: "l-edit",
                      title: "Edit Case",
                      pane: "Client",
                      path: "/client/competition/[id]/edit",
                      overlays: [
                        { label: "Deliverables list", endpoint: "/client/competition/[id]/deliverables" },
                        { label: "New deliverable", endpoint: "/client/competition/[id]/deliverable/new" },
                        { label: "Rubric editor" },
                      ],
                    },
                  ],
                },
                {
                  id: "l-dashboard",
                  title: "Live Dashboard",
                  pane: "Client",
                  path: "/client/competition/[id]/dashboard",
                  children: [
                    {
                      id: "l-candidates",
                      title: "Candidates",
                      pane: "Client",
                      path: "/client/competition/[id]/candidates",
                    },
                    {
                      id: "l-pipeline",
                      title: "Pipeline",
                      pane: "Client",
                      path: "/client/competition/[id]/pipeline",
                    },
                    {
                      id: "l-shortlist",
                      title: "Shortlist",
                      pane: "Client",
                      path: "/client/competition/[id]/shortlist",
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
  ],
};

/**
 * MANAGED CLIENT — Sign Up → Welcome → Book Intake (external) →
 *                  Coravu builds (admin) → Programs Grid → RM Dashboard → [Pipeline, Shortlist]
 */
const MANAGED_TREE: TreeNode = {
  id: "m-signup",
  title: "Sign Up",
  pane: "General",
  path: "/signup/client",
  children: [
    {
      id: "m-welcome",
      title: "Welcome",
      pane: "Client",
      path: "/client/welcome",
      children: [
        {
          id: "m-intake",
          title: "Book Intake",
          pane: "Client",
          external: true,
          externalUrl: "https://outlook.office.com/book/CoravuIntake@Coravu.com/",
          children: [
            {
              id: "m-coravu-build",
              title: "Coravu Builds",
              pane: "Admin",
              path: "/admin/programs/new-rm",
              children: [
                {
                  id: "m-programs",
                  title: "Programs Grid",
                  pane: "Client",
                  path: "/client",
                  overlays: [
                    { label: "Company", endpoint: "/client/company" },
                    { label: "Team", endpoint: "/client/team" },
                    { label: "Billing", endpoint: "/client/billing" },
                  ],
                  children: [
                    {
                      id: "m-dashboard",
                      title: "RM Dashboard",
                      pane: "Client",
                      path: "/client/competition/[id]/dashboard",
                      children: [
                        {
                          id: "m-candidates",
                          title: "Candidates",
                          pane: "Client",
                          path: "/client/competition/[id]/candidates",
                        },
                        {
                          id: "m-pipeline",
                          title: "Pipeline",
                          pane: "Client",
                          path: "/client/competition/[id]/pipeline",
                        },
                        {
                          id: "m-shortlist",
                          title: "Shortlist",
                          pane: "Client",
                          path: "/client/competition/[id]/shortlist",
                        },
                      ],
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
  ],
};

/**
 * ADMIN — Invited → Sign Up → Login + MFA → Admin Home → [Inbox, CRM, Programs (→ Build RM), Platform]
 */
const ADMIN_TREE: TreeNode = {
  id: "a-signup",
  title: "Invited / Sign Up",
  pane: "General",
  path: "/signup/employee",
  children: [
    {
      id: "a-login",
      title: "Login + MFA",
      pane: "General",
      path: "/login",
      overlays: [
        { label: "MFA challenge", endpoint: "/login/mfa-challenge" },
        { label: "Forgot password", endpoint: "/forgot-password" },
      ],
      children: [
        {
          id: "a-home",
          title: "Admin Home",
          pane: "Admin",
          path: "/admin",
          overlays: [
            { label: "Global search" },
            { label: "Notifications" },
            { label: "Account & security", endpoint: "/admin/account" },
          ],
          children: [
            {
              id: "a-inbox",
              title: "Inbox",
              pane: "Admin",
              path: "/admin/inquiries",
              overlays: [
                { label: "Case approvals", endpoint: "/admin/case-approvals" },
                { label: "Invites", endpoint: "/admin/invites" },
                { label: "Pending", endpoint: "/admin/pending" },
                { label: "Rejected", endpoint: "/admin/rejected" },
                { label: "Student review", endpoint: "/admin/students/review" },
              ],
            },
            {
              id: "a-crm",
              title: "CRM",
              pane: "Admin",
              path: "/admin/students",
              overlays: [
                { label: "Companies", endpoint: "/admin/clients" },
                { label: "Judges", endpoint: "/admin/judges" },
                { label: "Calendar", endpoint: "/admin/calendar" },
              ],
            },
            {
              id: "a-programs",
              title: "Programs",
              pane: "Admin",
              path: "/admin/competitions",
              overlays: [
                { label: "Winners", endpoint: "/admin/competitions/[id]/winners" },
                { label: "Announcements", endpoint: "/admin/announcements" },
                { label: "Disbursements", endpoint: "/admin/disbursements" },
              ],
              children: [
                {
                  id: "a-rm",
                  title: "Build RM Program",
                  pane: "Admin",
                  path: "/admin/programs/new-rm",
                },
              ],
            },
            {
              id: "a-platform",
              title: "Platform",
              pane: "Admin",
              path: "/admin/audit-log",
              overlays: [
                { label: "Users + roles", endpoint: "/admin/users" },
                { label: "Internal team", endpoint: "/admin/team" },
                { label: "Security", endpoint: "/admin/security" },
                { label: "MFA enroll", endpoint: "/admin/mfa/enroll" },
              ],
            },
          ],
        },
      ],
    },
  ],
};

/**
 * JUDGE — Sign Up → Login → Assignments queue → Competition Overview →
 *         Score Submission. Profile/Settings hang off the home as overlays.
 */
const JUDGE_TREE: TreeNode = {
  id: "j-signup",
  title: "Sign Up",
  pane: "General",
  path: "/signup/judge",
  children: [
    {
      id: "j-login",
      title: "Login + MFA",
      pane: "General",
      path: "/login",
      overlays: [
        { label: "MFA challenge", endpoint: "/login/mfa-challenge" },
        { label: "Forgot password", endpoint: "/forgot-password" },
        { label: "Confirm email", endpoint: "/confirm" },
      ],
      children: [
        {
          id: "j-home",
          title: "Assignments Queue",
          pane: "Judge",
          path: "/judge",
          overlays: [
            { label: "Profile", endpoint: "/judge/profile" },
            { label: "Settings & notification prefs", endpoint: "/judge/settings" },
          ],
          children: [
            {
              id: "j-comp",
              title: "Competition Overview",
              pane: "Judge",
              path: "/judge/competition/[id]",
              overlays: [
                { label: "Student profile (read-only)", endpoint: "/judge/student/[id]" },
              ],
              children: [
                {
                  id: "j-score",
                  title: "Score Submission",
                  pane: "Judge",
                  path: "/judge/competition/[id]/score/[submissionId]",
                },
              ],
            },
          ],
        },
      ],
    },
  ],
};

const TREES: Record<FlowMode, TreeNode> = {
  all: ALL_TREE,
  student: STUDENT_TREE,
  licensed: LICENSED_TREE,
  managed: MANAGED_TREE,
  admin: ADMIN_TREE,
  judge: JUDGE_TREE,
};

/* ─────────────────────── Geometry ─────────────────────── */

const CHART_WIDTH = 1280;
const CHART_HEIGHT_MIN = 520;

const CARD_W = 160;
const CARD_H = 38;
const COL_GAP = 40;
const COL_W = CARD_W + COL_GAP; // 200
const COL_X = [40, 240, 440, 640, 840, 1040, 1240];
const VERT_GAP = 14;

/* ─────────────────────── Tree layout (recursive) ─────────────────────── */

type Position = { x: number; y: number };

function layoutTree(root: TreeNode): {
  positions: Map<string, Position>;
  totalHeight: number;
} {
  const positions = new Map<string, Position>();

  function place(node: TreeNode, depth: number, startY: number): number {
    const x = COL_X[Math.min(depth, COL_X.length - 1)];
    if (!node.children || node.children.length === 0) {
      positions.set(node.id, { x, y: startY });
      return CARD_H;
    }
    let y = startY;
    for (const child of node.children) {
      const childH = place(child, depth + 1, y);
      y += childH + VERT_GAP;
    }
    const subtreeBottom = y - VERT_GAP;
    const subtreeHeight = subtreeBottom - startY;
    // Center parent on first/last child centers
    const firstChild = node.children[0];
    const lastChild = node.children[node.children.length - 1];
    const firstPos = positions.get(firstChild.id)!;
    const lastPos = positions.get(lastChild.id)!;
    const firstCenter = firstPos.y + CARD_H / 2;
    const lastCenter = lastPos.y + CARD_H / 2;
    const parentCenter = (firstCenter + lastCenter) / 2;
    positions.set(node.id, { x, y: parentCenter - CARD_H / 2 });
    return subtreeHeight;
  }

  const totalHeight = place(root, 0, 0);
  return { positions, totalHeight };
}

/* ─────────────────────── Overlay shell ─────────────────────── */

export function RouteOverlay({
  visible,
  onClose,
  activePane,
}: {
  visible: boolean;
  onClose: () => void;
  activePane: PaneKey;
}) {
  const [openSubItemsId, setOpenSubItemsId] = useState<string | null>(null);
  const [legendIndex, setLegendIndex] = useState<PaneOrAux | null>(null);
  const [mode, setMode] = useState<FlowMode>("all");

  const closeAll = () => {
    setOpenSubItemsId(null);
    setLegendIndex(null);
    onClose();
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={closeAll}>
      <Pressable
        onPress={closeAll}
        style={{
          flex: 1,
          backgroundColor: "rgba(0,0,0,0.75)",
          padding: 24,
          ...(Platform.OS === "web"
            ? ({ backdropFilter: "blur(12px)" } as unknown as object)
            : null),
        }}
      >
        <Pressable
          onPress={(e) => {
            e.stopPropagation();
            setOpenSubItemsId(null);
          }}
          style={[
            {
              flex: 1,
              backgroundColor: PAGE_BG,
              borderRadius: 16,
              borderWidth: 1,
              borderColor: HEADER_BORDER,
              overflow: "hidden",
              position: "relative",
            },
            Platform.OS === "web"
              ? ({ boxShadow: "0 32px 80px rgba(0,0,0,0.55)" } as unknown as ViewStyle)
              : null,
          ]}
        >
          {/* Close */}
          <Pressable
            onPress={closeAll}
            accessibilityRole="button"
            style={({ hovered }) => [
              {
                position: "absolute",
                top: 16,
                right: 16,
                width: 30,
                height: 30,
                borderRadius: 999,
                borderWidth: 1,
                borderColor: BUTTON_BORDER,
                alignItems: "center",
                justifyContent: "center",
                zIndex: 220,
                backgroundColor: HEADER_BG,
              },
              Platform.OS === "web" && hovered
                ? ({ backgroundColor: BUTTON_HOVER_BG } as ViewStyle)
                : null,
            ]}
          >
            <Text style={{ fontSize: 15, color: TEXT_DIM, lineHeight: 15 }}>×</Text>
          </Pressable>

          {/* Top: legend pill row */}
          <View style={{ alignItems: "center", paddingTop: 18, paddingBottom: 4 }}>
            <Pressable onPress={(e) => e.stopPropagation()}>
              <LegendPillRow activePane={activePane} onSelect={setLegendIndex} />
            </Pressable>
          </View>

          {/* Chart */}
          <ScrollView
            style={{ flex: 1, width: "100%" }}
            contentContainerStyle={{ alignItems: "center", justifyContent: "center", flexGrow: 1, paddingVertical: 8 }}
            showsVerticalScrollIndicator={false}
          >
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={{ alignItems: "center" }}
            >
              <TreeChart
                tree={TREES[mode]}
                openSubItemsId={openSubItemsId}
                setOpenSubItemsId={setOpenSubItemsId}
                activePane={activePane}
              />
            </ScrollView>
          </ScrollView>

          {/* Bottom: mode selector */}
          <View style={{ alignItems: "center", paddingBottom: 22, paddingTop: 6 }}>
            <Pressable onPress={(e) => e.stopPropagation()}>
              <ModeSelector value={mode} onChange={setMode} />
            </Pressable>
          </View>
        </Pressable>
      </Pressable>

      <LegendIndexModal pane={legendIndex} onClose={() => setLegendIndex(null)} />
    </Modal>
  );
}

/* ─────────────────────── Tree chart (multi-level) ─────────────────────── */

function TreeChart({
  tree,
  openSubItemsId,
  setOpenSubItemsId,
  activePane,
}: {
  tree: TreeNode;
  openSubItemsId: string | null;
  setOpenSubItemsId: (next: string | null | ((prev: string | null) => string | null)) => void;
  activePane: PaneKey;
}) {
  const { positions, totalHeight } = layoutTree(tree);
  const chartHeight = Math.max(CHART_HEIGHT_MIN, totalHeight + 40);
  const yOffset = Math.max(20, (chartHeight - totalHeight) / 2);

  // Flatten all nodes for rendering
  const flatNodes: TreeNode[] = [];
  function flatten(node: TreeNode) {
    flatNodes.push(node);
    if (node.children) for (const c of node.children) flatten(c);
  }
  flatten(tree);

  // Collect connector path segments per parent → children
  type Seg = { d: string; stroke: string; sw: number };
  const segs: Seg[] = [];
  function gatherSegs(node: TreeNode) {
    if (!node.children || node.children.length === 0) return;
    const parentPos = positions.get(node.id)!;
    const childPositions = node.children.map((c) => positions.get(c.id)!);
    const parentRight = parentPos.x + CARD_W;
    const parentCenter = parentPos.y + yOffset + CARD_H / 2;
    const childX = childPositions[0].x;
    const midX = (parentRight + childX) / 2;
    const childCenters = childPositions.map((p) => p.y + yOffset + CARD_H / 2);
    const topC = Math.min(...childCenters, parentCenter);
    const botC = Math.max(...childCenters, parentCenter);

    // Determine stroke color: use child pane accent if all children share, else CONNECTOR
    const allSamePane = node.children.every((c) => c.pane === node.children![0].pane);
    const stroke =
      allSamePane && node.children[0].pane && node.children[0].pane !== "Aux"
        ? activePane === node.children[0].pane
          ? PANE_ACCENT[node.children[0].pane]
          : CONNECTOR
        : CONNECTOR;
    const sw = stroke !== CONNECTOR ? 2 : 1.5;

    // 1. parent → midX horizontal
    segs.push({
      d: `M ${parentRight} ${parentCenter} L ${midX} ${parentCenter}`,
      stroke,
      sw,
    });
    // 2. vertical bar at midX
    segs.push({
      d: `M ${midX} ${topC} L ${midX} ${botC}`,
      stroke,
      sw,
    });
    // 3. midX → each child + arrowhead
    childCenters.forEach((cy) => {
      segs.push({
        d: `M ${midX} ${cy} L ${childX} ${cy}`,
        stroke,
        sw,
      });
      segs.push({
        d: `M ${childX - 5} ${cy - 4} L ${childX} ${cy} L ${childX - 5} ${cy + 4}`,
        stroke,
        sw,
      });
    });

    // Recurse
    for (const child of node.children) gatherSegs(child);
  }
  gatherSegs(tree);

  return (
    <View
      style={{
        width: CHART_WIDTH,
        height: chartHeight,
        position: "relative",
      }}
    >
      <View
        pointerEvents="none"
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          width: CHART_WIDTH,
          height: chartHeight,
        }}
      >
        <Svg width={CHART_WIDTH} height={chartHeight}>
          {segs.map((s, i) => (
            <Path key={i} d={s.d} stroke={s.stroke} strokeWidth={s.sw} fill="none" strokeLinecap="round" strokeLinejoin="round" />
          ))}
        </Svg>
      </View>

      {flatNodes.map((node) => {
        const pos = positions.get(node.id)!;
        return (
          <NodeCard
            key={node.id}
            card={node}
            x={pos.x}
            y={pos.y + yOffset}
            w={CARD_W}
            h={CARD_H}
            expanded={openSubItemsId === node.id}
            onToggle={() =>
              setOpenSubItemsId((prev) => (prev === node.id ? null : node.id))
            }
            paneActive={
              node.pane === "General"
                ? activePane === "General"
                : activePane === node.pane
            }
          />
        );
      })}
    </View>
  );
}

/* ─────────────────────── Mode selector (bottom) ─────────────────────── */

const MODE_SLOT_HEIGHT = 32;
const MODE_SLOT_PAD = 3;

function ModeSelector({ value, onChange }: { value: FlowMode; onChange: (next: FlowMode) => void }) {
  const [widths, setWidths] = useState<number[]>(Array(MODES.length).fill(0));
  const indicatorX = useSharedValue(0);
  const indicatorW = useSharedValue(0);

  useEffect(() => {
    const idx = MODES.findIndex((m) => m.id === value);
    if (idx < 0) return;
    const xs = widths.slice(0, idx).reduce((a, b) => a + b, 0);
    indicatorX.value = withTiming(xs, {
      duration: 320,
      easing: Easing.bezier(0.32, 0.72, 0, 1),
    });
    indicatorW.value = withTiming(widths[idx] ?? 0, {
      duration: 320,
      easing: Easing.bezier(0.32, 0.72, 0, 1),
    });
  }, [value, widths, indicatorX, indicatorW]);

  const indicatorStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: indicatorX.value }],
    width: indicatorW.value,
  }));

  return (
    <View
      style={{
        flexDirection: "row",
        backgroundColor: HEADER_BG,
        borderRadius: 999,
        padding: MODE_SLOT_PAD,
        borderWidth: 1,
        borderColor: HEADER_BORDER,
        position: "relative",
      }}
    >
      <Animated.View
        pointerEvents="none"
        style={[
          {
            position: "absolute",
            top: MODE_SLOT_PAD,
            left: MODE_SLOT_PAD,
            height: MODE_SLOT_HEIGHT,
            borderRadius: 999,
            backgroundColor: AMBER_BG_ACTIVE,
            borderWidth: 1,
            borderColor: AMBER_BORDER_ACTIVE,
            ...(Platform.OS === "web"
              ? ({ boxShadow: "0 2px 12px rgba(181,135,58,0.25)" } as unknown as ViewStyle)
              : null),
          },
          indicatorStyle,
        ]}
      />
      {MODES.map((m, i) => {
        const active = m.id === value;
        return (
          <Pressable
            key={m.id}
            accessibilityRole="button"
            onPress={() => onChange(m.id)}
            onLayout={(e) => {
              const w = e.nativeEvent.layout.width;
              setWidths((prev) => {
                if (prev[i] === w) return prev;
                const next = [...prev];
                next[i] = w;
                return next;
              });
            }}
            style={({ hovered }) => [
              {
                height: MODE_SLOT_HEIGHT,
                paddingHorizontal: 16,
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 999,
              },
              Platform.OS === "web" && hovered && !active
                ? ({ backgroundColor: BUTTON_HOVER_BG } as unknown as ViewStyle)
                : null,
              Platform.OS === "web" ? ({ cursor: "pointer" } as unknown as ViewStyle) : null,
            ]}
          >
            <Text
              style={{
                fontFamily: "DMSans_600SemiBold",
                fontSize: 11,
                letterSpacing: 1.4,
                textTransform: "uppercase",
                color: active ? AMBER : TEXT_DIM,
              }}
            >
              {m.label}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}

/* ─────────────────────── Legend pill row ─────────────────────── */

function LegendPillRow({
  activePane,
  onSelect,
}: {
  activePane: PaneKey;
  onSelect: (pane: PaneOrAux) => void;
}) {
  return (
    <View
      style={{
        flexDirection: "row",
        backgroundColor: HEADER_BG,
        borderRadius: 999,
        borderWidth: 1,
        borderColor: HEADER_BORDER,
        padding: 5,
        gap: 4,
      }}
    >
      {(["General", "Student", "Client", "Admin", "Judge", "Aux"] as PaneOrAux[]).map((p) => (
        <LegendPill key={p} pane={p} active={p === activePane} onPress={() => onSelect(p)} />
      ))}
    </View>
  );
}

function LegendPill({
  pane,
  active,
  onPress,
}: {
  pane: PaneOrAux;
  active: boolean;
  onPress: () => void;
}) {
  const accent = PANE_ACCENT[pane];
  const label = pane === "Aux" ? "External" : PANE_LABELS[pane as PaneKey].label;
  return (
    <Pressable
      accessibilityRole="button"
      onPress={onPress}
      style={({ hovered }) => [
        {
          flexDirection: "row",
          alignItems: "center",
          gap: 7,
          paddingVertical: 6,
          paddingHorizontal: 12,
          borderRadius: 999,
          borderWidth: 1,
          borderColor: active ? AMBER_BORDER_ACTIVE : "transparent",
          backgroundColor: active ? AMBER_BG_ACTIVE : "transparent",
        },
        Platform.OS === "web" && hovered && !active
          ? ({ backgroundColor: BUTTON_HOVER_BG, cursor: "pointer" } as unknown as ViewStyle)
          : null,
        Platform.OS === "web" ? ({ cursor: "pointer" } as unknown as ViewStyle) : null,
      ]}
    >
      <View
        style={{
          width: 18,
          height: 18,
          borderRadius: 4,
          backgroundColor: accent,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <PaneGlyph pane={pane} size={11} color={COLORS.WHITE.hex} />
      </View>
      <Text
        style={{
          fontFamily: "DMSans_600SemiBold",
          fontSize: 11,
          letterSpacing: 1.2,
          textTransform: "uppercase",
          color: active ? AMBER : TEXT_DIM,
        }}
      >
        {label}
      </Text>
    </Pressable>
  );
}

/* ─────────────────────── Node card ─────────────────────── */

function NodeCard({
  card,
  x,
  y,
  w,
  h,
  expanded,
  onToggle,
  paneActive,
}: {
  card: TreeNode;
  x: number;
  y: number;
  w: number;
  h: number;
  expanded: boolean;
  onToggle: () => void;
  paneActive: boolean;
}) {
  const accent = PANE_ACCENT[card.pane];
  const lastClickRef = useRef(0);
  const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [hovered, setHovered] = useState(false);
  const isExternal = !!card.external;
  const hasOverlays = !!card.overlays?.length;

  const handlePress = () => {
    const now = Date.now();
    if (now - lastClickRef.current < 320) {
      if (clickTimerRef.current) {
        clearTimeout(clickTimerRef.current);
        clickTimerRef.current = null;
      }
      if (isExternal && card.externalUrl) {
        if (Platform.OS === "web" && typeof window !== "undefined") {
          window.open(card.externalUrl, "_blank");
        } else {
          Linking.openURL(card.externalUrl).catch(() => {});
        }
      } else if (card.path) {
        openRoute(card.path);
      }
      lastClickRef.current = 0;
      return;
    }
    lastClickRef.current = now;
    if (!hasOverlays) {
      if (isExternal && card.externalUrl) {
        if (Platform.OS === "web" && typeof window !== "undefined") {
          window.open(card.externalUrl, "_blank");
        } else {
          Linking.openURL(card.externalUrl).catch(() => {});
        }
      } else if (card.path) {
        openRoute(card.path);
      }
      return;
    }
    if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
    clickTimerRef.current = setTimeout(() => {
      onToggle();
      clickTimerRef.current = null;
    }, 320);
  };

  return (
    <>
      <Pressable
        accessibilityRole="button"
        onPress={handlePress}
        onHoverIn={() => setHovered(true)}
        onHoverOut={() => setHovered(false)}
        style={[
          {
            position: "absolute",
            left: x,
            top: y,
            width: w,
            height: h,
            backgroundColor: isExternal ? "transparent" : accent,
            borderRadius: 6,
            borderWidth: isExternal ? 1 : paneActive ? 2 : 1,
            borderColor: isExternal ? accent : paneActive ? "rgba(247,244,238,0.85)" : accent,
            borderStyle: isExternal ? "dashed" : "solid",
            paddingHorizontal: 8,
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
          },
          Platform.OS === "web"
            ? ({
                boxShadow: isExternal
                  ? "none"
                  : hovered
                    ? "4px 5px 12px rgba(0,0,0,0.45)"
                    : "3px 4px 8px rgba(0,0,0,0.35)",
                transform: hovered ? "translate(-1px,-1px)" : "translate(0,0)",
                transitionProperty: "box-shadow, transform, background-color, border-color",
                transitionDuration: "180ms",
                cursor: "pointer",
              } as unknown as ViewStyle)
            : null,
        ]}
      >
        {isExternal ? <ExternalIcon color={accent} /> : null}
        <Text
          style={{
            fontFamily: "DMSans_600SemiBold",
            fontSize: 11.5,
            color: isExternal ? accent : COLORS.WHITE.hex,
            textAlign: "center",
            fontStyle: isExternal ? "italic" : "normal",
            marginLeft: isExternal ? 4 : 0,
            flexShrink: 1,
          }}
          numberOfLines={1}
        >
          {card.title}
        </Text>
        {hasOverlays ? (
          <View style={{ marginLeft: 6 }}>
            <Chevron color={isExternal ? accent : "rgba(255,255,255,0.85)"} open={expanded} />
          </View>
        ) : null}
      </Pressable>

      {expanded && card.overlays?.length ? (
        <SubItemsPopover
          x={Math.min(x + w + 14, CHART_WIDTH - 260)}
          y={y - 10}
          width={260}
          subItems={card.overlays}
          accent={accent}
        />
      ) : null}
    </>
  );
}

function SubItemsPopover({
  x,
  y,
  width,
  subItems,
  accent,
}: {
  x: number;
  y: number;
  width: number;
  subItems: SubItem[];
  accent: string;
}) {
  return (
    <View
      style={[
        {
          position: "absolute",
          left: x,
          top: y,
          width,
          minWidth: 220,
          backgroundColor: HEADER_BG,
          borderRadius: 10,
          borderWidth: 1,
          borderColor: `${accent}55`,
          borderLeftWidth: 3,
          borderLeftColor: accent,
          paddingVertical: 8,
          paddingHorizontal: 10,
          gap: 2,
          zIndex: 100,
        },
        Platform.OS === "web"
          ? ({ boxShadow: "0 12px 28px rgba(0,0,0,0.55)" } as unknown as ViewStyle)
          : null,
      ]}
    >
      {subItems.map((s) => (
        <View
          key={s.label}
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 6,
            paddingHorizontal: 4,
            paddingVertical: 3,
          }}
        >
          <ArrowGlyph color={accent} />
          <Text
            style={{ fontFamily: "DMSans_500Medium", fontSize: 10.5, color: TEXT, flexShrink: 1 }}
            numberOfLines={1}
          >
            {s.label}
          </Text>
          {s.endpoint ? (
            <Text
              style={{ fontFamily: MONO_FONT, fontSize: 9.5, color: TEXT_FAINT, flexShrink: 1 }}
              numberOfLines={1}
            >
              {s.endpoint}
            </Text>
          ) : null}
        </View>
      ))}
    </View>
  );
}

/* ─────────────────────── Style index modal ─────────────────────── */

function LegendIndexModal({
  pane,
  onClose,
}: {
  pane: PaneOrAux | null;
  onClose: () => void;
}) {
  if (!pane) return null;
  const accent = PANE_ACCENT[pane];
  const label = pane === "Aux" ? "External" : PANE_LABELS[pane as PaneKey].label;
  const role =
    pane === "Aux"
      ? "Public-facing legal + utility surfaces. Not part of any customer flow."
      : PANE_LABELS[pane as PaneKey].role;
  const routes =
    pane === "Aux"
      ? [
          { path: "/privacy", description: "Privacy policy" },
          { path: "/terms", description: "Terms of service" },
          { path: "/accept-invite", description: "Accept-invite landing" },
          { path: "/brand", description: "Internal brand reference (this page)" },
          { path: "/contact", description: "Contact / book intake" },
        ]
      : ROUTE_MAP[pane as PaneKey];

  return (
    <Modal visible transparent animationType="fade" onRequestClose={onClose}>
      <Pressable
        onPress={onClose}
        style={{
          flex: 1,
          backgroundColor: "rgba(0,0,0,0.7)",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
          ...(Platform.OS === "web" ? ({ backdropFilter: "blur(10px)" } as unknown as object) : null),
        }}
      >
        <Pressable
          onPress={(e) => e.stopPropagation()}
          style={[
            {
              width: "100%",
              maxWidth: 680,
              maxHeight: "80%",
              backgroundColor: PAGE_BG,
              borderRadius: 14,
              borderWidth: 1,
              borderColor: HEADER_BORDER,
              overflow: "hidden",
            },
            Platform.OS === "web"
              ? ({ boxShadow: "0 32px 80px rgba(0,0,0,0.55)" } as unknown as ViewStyle)
              : null,
          ]}
        >
          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              paddingVertical: 14,
              paddingHorizontal: 18,
              borderBottomWidth: 1,
              borderBottomColor: HEADER_BORDER,
              backgroundColor: HEADER_BG,
            }}
          >
            <View style={{ flexDirection: "row", alignItems: "center", gap: 12, flex: 1 }}>
              <View
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 7,
                  backgroundColor: accent,
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <PaneGlyph pane={pane} size={18} color={COLORS.WHITE.hex} />
              </View>
              <View style={{ flex: 1 }}>
                <Text
                  style={{
                    fontFamily: "DMSans_700Bold",
                    fontSize: 9.5,
                    letterSpacing: 2.2,
                    textTransform: "uppercase",
                    color: accent,
                  }}
                >
                  Style index
                </Text>
                <Text style={{ fontFamily: "CormorantGaramond_500Medium", fontSize: 19, color: TEXT }}>
                  {label}
                </Text>
                <Text
                  style={{
                    fontFamily: "DMSans_400Regular",
                    fontSize: 11,
                    color: TEXT_DIM,
                    lineHeight: 16,
                  }}
                >
                  {role}
                </Text>
              </View>
            </View>
            <Pressable
              onPress={onClose}
              accessibilityRole="button"
              style={({ hovered }) => [
                {
                  width: 28,
                  height: 28,
                  borderRadius: 999,
                  borderWidth: 1,
                  borderColor: BUTTON_BORDER,
                  alignItems: "center",
                  justifyContent: "center",
                },
                Platform.OS === "web" && hovered
                  ? ({ backgroundColor: BUTTON_HOVER_BG } as ViewStyle)
                  : null,
              ]}
            >
              <Text style={{ fontSize: 14, color: TEXT_DIM, lineHeight: 14 }}>×</Text>
            </Pressable>
          </View>
          <ScrollView contentContainerStyle={{ padding: 16 }}>
            {routes.map((r, i) => (
              <View
                key={r.path}
                style={{
                  paddingVertical: 7,
                  borderTopWidth: i === 0 ? 0 : 1,
                  borderTopColor: "rgba(247,244,238,0.08)",
                  gap: 2,
                }}
              >
                <Text style={{ fontFamily: "DMSans_500Medium", fontSize: 12, color: TEXT }}>
                  {r.description ?? r.path}
                </Text>
                <Text style={{ fontFamily: MONO_FONT, fontSize: 10.5, color: TEXT_FAINT }}>
                  {r.path}
                </Text>
              </View>
            ))}
          </ScrollView>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

/* ─────────────────────── Helpers ─────────────────────── */

function openRoute(path: string) {
  const resolved = path.replace(/\[[^\]]+\]/g, "1");
  if (Platform.OS === "web" && typeof window !== "undefined") {
    window.open(resolved, "_blank");
  } else {
    Linking.openURL(`http://localhost:8081${resolved}`).catch(() => {});
  }
}

/* ─────────────────────── Icons ─────────────────────── */

function ArrowGlyph({ color }: { color: string }) {
  return (
    <Svg width={9} height={9} viewBox="0 0 12 12" fill="none">
      <Path
        d="M3 2 L3 6 L9 6 M6 3 L9 6 L6 9"
        stroke={color}
        strokeWidth={1.4}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  );
}

function Chevron({ color, open }: { color: string; open: boolean }) {
  return (
    <Svg
      width={9}
      height={9}
      viewBox="0 0 12 12"
      fill="none"
      style={{ transform: [{ rotate: open ? "180deg" : "0deg" }] }}
    >
      <Path d="M2 4 L6 8 L10 4" stroke={color} strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

function ExternalIcon({ color }: { color: string }) {
  return (
    <Svg width={10} height={10} viewBox="0 0 12 12" fill="none">
      <Path d="M5 2 H10 V7" stroke={color} strokeWidth={1.4} strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M10 2 L5 7" stroke={color} strokeWidth={1.4} strokeLinecap="round" />
      <Path d="M3 4 V9 H8" stroke={color} strokeWidth={1.4} strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

function PaneGlyph({ pane, size, color }: { pane: PaneOrAux; size: number; color?: string }) {
  const c = color ?? PANE_ACCENT[pane];
  switch (pane) {
    case "General":
      return <GlobeGlyph color={c} size={size} />;
    case "Student":
      return <CapGlyph color={c} size={size} />;
    case "Client":
      return <BriefcaseGlyph color={c} size={size} />;
    case "Admin":
      return <EyeGlyph color={c} size={size} />;
    case "Judge":
      return <ClipboardCheckGlyph color={c} size={size} />;
    default:
      return <DotsGlyph color={c} size={size} />;
  }
}

function ClipboardCheckGlyph({ color, size }: { color: string; size: number }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Rect x={5} y={4} width={14} height={17} rx={2} stroke={color} strokeWidth={1.6} />
      <Rect x={9} y={2} width={6} height={3.5} rx={1} stroke={color} strokeWidth={1.6} />
      <Path
        d="M8.5 12.5 L11 15 L15.5 9.5"
        stroke={color}
        strokeWidth={1.6}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  );
}

function GlobeGlyph({ color, size }: { color: string; size: number }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Circle cx={12} cy={12} r={9} stroke={color} strokeWidth={1.6} />
      <Path d="M3 12 H21" stroke={color} strokeWidth={1.4} />
      <Path d="M12 3 C 16 7, 16 17, 12 21" stroke={color} strokeWidth={1.4} fill="none" />
      <Path d="M12 3 C 8 7, 8 17, 12 21" stroke={color} strokeWidth={1.4} fill="none" />
    </Svg>
  );
}

function CapGlyph({ color, size }: { color: string; size: number }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path d="M2 9 L12 4 L22 9 L12 14 Z" stroke={color} strokeWidth={1.6} strokeLinejoin="round" />
      <Path d="M6 11 V16 C 6 18, 18 18, 18 16 V11" stroke={color} strokeWidth={1.6} fill="none" />
      <Path d="M22 9 V14" stroke={color} strokeWidth={1.4} />
    </Svg>
  );
}

function BriefcaseGlyph({ color, size }: { color: string; size: number }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Rect x={3} y={7} width={18} height={13} rx={2} stroke={color} strokeWidth={1.6} />
      <Path
        d="M9 7 V5 C 9 4, 10 3, 11 3 H13 C 14 3, 15 4, 15 5 V7"
        stroke={color}
        strokeWidth={1.6}
        strokeLinejoin="round"
      />
      <Path d="M3 13 H21" stroke={color} strokeWidth={1.4} />
    </Svg>
  );
}

function EyeGlyph({ color, size }: { color: string; size: number }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path
        d="M 2 12 C 5.5 6, 18.5 6, 22 12 C 18.5 18, 5.5 18, 2 12 Z"
        stroke={color}
        strokeWidth={1.6}
        strokeLinejoin="round"
        fill="none"
      />
      <Circle cx={12} cy={12} r={3.5} stroke={color} strokeWidth={1.5} fill="none" />
      <Circle cx={12} cy={12} r={1.5} fill={color} />
    </Svg>
  );
}

function DotsGlyph({ color, size }: { color: string; size: number }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Circle cx={5} cy={12} r={1.8} fill={color} />
      <Circle cx={12} cy={12} r={1.8} fill={color} />
      <Circle cx={19} cy={12} r={1.8} fill={color} />
    </Svg>
  );
}

export function CoravuSunIcon({ size = 22, color }: { size?: number; color?: string }) {
  const c = color ?? COLORS.AMBER.hex;
  return (
    <View
      style={{
        width: size,
        height: size,
        borderRadius: 999,
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <View
        style={{
          position: "absolute",
          width: size,
          height: size,
          borderRadius: 999,
          borderWidth: 0.8,
          borderColor: COLORS.SAGE.hex,
        }}
      />
      <View
        style={{
          width: size * 0.6,
          height: size * 0.6,
          borderRadius: 999,
          borderWidth: 1.2,
          borderColor: c,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <View
          style={{
            width: size * 0.22,
            height: size * 0.22,
            borderRadius: 999,
            backgroundColor: c,
          }}
        />
      </View>
    </View>
  );
}
